
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AssignmentIcon from "@mui/icons-material/Assignment";
import EditNoteIcon from "@mui/icons-material/EditNote";
import PageContainer from "../common/PageContainer";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const TimesheetForm = () => {
  const [date, setDate] = useState("");
  const [taskDetails, setTaskDetails] = useState([{ taskName: "", duration: "" }]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [totalHours, setTotalHours] = useState(0);

  useEffect(() => {
    const sum = taskDetails.reduce((acc, task) => {
      const duration = parseFloat(task.duration) || 0;
      return acc + duration;
    }, 0);
    setTotalHours(sum);
  }, [taskDetails]);

  const handleTaskChange = (index, field, value) => {
    const updatedTasks = [...taskDetails];
    if (field === "duration") {
      if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) {
        updatedTasks[index][field] = value;
      }
    } else {
      updatedTasks[index][field] = value;
    }
    setTaskDetails(updatedTasks);
  };

  const addTask = () => {
    setTaskDetails([...taskDetails, { taskName: "", duration: "" }]);
  };

  const removeTask = (index) => {
    const updatedTasks = taskDetails.filter((_, i) => i !== index);
    setTaskDetails(updatedTasks);
  };

  const validateForm = () => {
    if (taskDetails.length === 0) {
      setError("Please add at least one task");
      return false;
    }

    for (const task of taskDetails) {
      if (task.duration === "" || isNaN(parseFloat(task.duration))) {
        setError("Please enter valid numbers for all task durations");
        return false;
      }

      if (!task.taskName.trim()) {
        setError("Please enter task names for all tasks");
        return false;
      }
    }

    if (totalHours <= 0) {
      setError("Total hours must be greater than 0");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const token = sessionStorage.getItem("token");
      if (!token) {
        setError("No token found. Please log in.");
        return;
      }

      const requestBody = {
        date,
        hoursWorked: totalHours,
        taskDetails: taskDetails.map((task) => ({
          taskName: task.taskName,
          duration: parseFloat(task.duration),
        })),
      };

      const response = await fetch(`${BASE_URL}/api/timesheet/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.msg || "Error creating timesheet");
      }

      setMessage(data.msg);
      setError("");
      setDate("");
      setTaskDetails([{ taskName: "", duration: "" }]);
    } catch (err) {
      setError(err.message || "Error creating timesheet");
      setMessage("");
    }
  };

  const taskAccentColors = ["#667eea", "#43e97b", "#f7971e", "#f093fb", "#4facfe"];
  const taskBgColors = ["#eef0ff", "#e8faf0", "#fff5e6", "#fce8fd", "#e8f4ff"];

  return (
    <PageContainer>
      {/* Hero Gradient Banner */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          borderRadius: "20px",
          p: { xs: 3, sm: 4 },
          mb: 3,
          color: "#fff",
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 8px 32px rgba(102, 126, 234, 0.35)",
          "&::before": {
            content: '""',
            position: "absolute",
            top: -60,
            right: -60,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: -40,
            left: "30%",
            width: 150,
            height: 150,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.05)",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, zIndex: 1, position: "relative" }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "16px",
              bgcolor: "rgba(255,255,255,0.18)",
              backdropFilter: "blur(10px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AccessTimeIcon sx={{ fontSize: 30, color: "#fff" }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: { xs: "1.4rem", sm: "1.7rem" }, lineHeight: 1.2 }}>
              Timesheet Portal
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.75)", mt: 0.5, fontSize: "0.9rem" }}>
              Log your daily work hours and tasks
            </Typography>
          </Box>
        </Box>
        {date && (
          <Box
            sx={{
              position: "absolute",
              top: 20,
              right: 24,
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
              gap: 1,
              bgcolor: "rgba(255,255,255,0.15)",
              backdropFilter: "blur(8px)",
              borderRadius: "12px",
              px: 2,
              py: 1,
            }}
          >
            <CalendarTodayIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.8)" }} />
            <Typography sx={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.9)", fontWeight: 500 }}>
              {new Date(date).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
        }}
      >
        {/* Left Section - Task Preview */}
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            border: "1px solid #eef0f6",
            borderRadius: "16px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 420,
            boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
          }}
        >
          {/* Section title bar */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              px: 3,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <AssignmentIcon sx={{ fontSize: 20, color: "#fff" }} />
            <Typography sx={{ fontWeight: 600, fontSize: "0.95rem", color: "#fff" }}>
              Task Preview
            </Typography>
            <Box
              sx={{
                ml: "auto",
                bgcolor: "rgba(255,255,255,0.2)",
                borderRadius: "8px",
                px: 1.5,
                py: 0.25,
                fontSize: "0.75rem",
                fontWeight: 600,
                color: "#fff",
              }}
            >
              {taskDetails.length} {taskDetails.length === 1 ? "task" : "tasks"}
            </Box>
          </Box>

          <Box sx={{ p: 3, flex: 1, display: "flex", flexDirection: "column" }}>
            {taskDetails.length === 0 ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #eef0f6, #e8eaf6)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1,
                  }}
                >
                  <AssignmentIcon sx={{ fontSize: 28, color: "#8b8fa3" }} />
                </Box>
                <Typography sx={{ color: "#8b8fa3", fontSize: "0.9rem", fontWeight: 500 }}>
                  No tasks added yet
                </Typography>
                <Typography sx={{ color: "#b0b4c8", fontSize: "0.8rem" }}>
                  Add tasks using the form on the right
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {taskDetails.map((task, idx) => {
                  const accentColor = taskAccentColors[idx % taskAccentColors.length];
                  const bgColor = taskBgColors[idx % taskBgColors.length];
                  return (
                    <Box
                      key={idx}
                      sx={{
                        p: 2,
                        borderRadius: "12px",
                        bgcolor: "#fff",
                        border: "1px solid #eef0f6",
                        borderLeft: `4px solid ${accentColor}`,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          boxShadow: "0 4px 14px rgba(0,0,0,0.07)",
                          transform: "translateY(-1px)",
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "8px",
                            bgcolor: bgColor,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            color: accentColor,
                          }}
                        >
                          #{idx + 1}
                        </Box>
                        <Box>
                          <Typography
                            sx={{ fontWeight: 600, color: "#1a1a2e", fontSize: "0.9rem" }}
                          >
                            {task.taskName || "Untitled Task"}
                          </Typography>
                          <Typography sx={{ color: "#8b8fa3", fontSize: "0.75rem", mt: 0.25 }}>
                            Task {idx + 1}
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          bgcolor: bgColor,
                          color: accentColor,
                          px: 1.5,
                          py: 0.5,
                          borderRadius: "20px",
                          fontWeight: 700,
                          fontSize: "0.8rem",
                          minWidth: 50,
                          textAlign: "center",
                        }}
                      >
                        {task.duration || "0"}h
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* Total Hours Summary */}
            {taskDetails.length > 0 && (
              <Box
                sx={{
                  mt: "auto",
                  pt: 2.5,
                }}
              >
                <Box
                  sx={{
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    borderRadius: "14px",
                    p: 2.5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    boxShadow: "0 4px 16px rgba(102, 126, 234, 0.3)",
                  }}
                >
                  <Box>
                    <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      Total Hours
                    </Typography>
                    <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "1.5rem", lineHeight: 1.2 }}>
                      {totalHours.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "12px",
                      bgcolor: "rgba(255,255,255,0.18)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AccessTimeIcon sx={{ color: "#fff", fontSize: 24 }} />
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Right Section - Timesheet Entry Form */}
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            border: "1px solid #eef0f6",
            borderRadius: "16px",
            overflow: "hidden",
            maxWidth: { md: 560 },
            boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
          }}
        >
          {/* Section title bar */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              px: 3,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <EditNoteIcon sx={{ fontSize: 20, color: "#fff" }} />
            <Typography sx={{ fontWeight: 600, fontSize: "0.95rem", color: "#fff" }}>
              Timesheet Entry
            </Typography>
          </Box>

          <Box sx={{ p: 3 }}>
            <form onSubmit={handleSubmit}>
              {/* Date Field */}
              <Box mb={2.5}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "#5a5f7a",
                    mb: 0.75,
                    fontSize: "0.8rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                  }}
                >
                  <CalendarTodayIcon sx={{ fontSize: 14, color: "#667eea" }} />
                  Date <span style={{ color: "#DC2626" }}>*</span>
                </Typography>
                <TextField
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  fullWidth
                  size="small"
                  required
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      backgroundColor: "#f5f7fb",
                      "& fieldset": { borderColor: "#eef0f6" },
                      "&:hover fieldset": { borderColor: "#CBD5E1" },
                      "&.Mui-focused fieldset": { borderColor: "#667eea" },
                    },
                  }}
                />
              </Box>

              {/* Total Hours Display - Gradient Card */}
              <Box
                sx={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  borderRadius: "14px",
                  p: 2.5,
                  mb: 2.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  boxShadow: "0 4px 16px rgba(102, 126, 234, 0.25)",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: -20,
                    right: -20,
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.08)",
                  },
                }}
              >
                <Box sx={{ zIndex: 1 }}>
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.7)",
                      fontWeight: 500,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Total Hours
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: "#fff",
                      fontSize: "2rem",
                      lineHeight: 1.2,
                    }}
                  >
                    {totalHours.toFixed(2)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: "14px",
                    bgcolor: "rgba(255,255,255,0.18)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1,
                  }}
                >
                  <AccessTimeIcon sx={{ color: "#fff", fontSize: 28 }} />
                </Box>
              </Box>

              {/* Task Details Section */}
              <Box
                sx={{
                  border: "1px solid #eef0f6",
                  borderRadius: "14px",
                  overflow: "hidden",
                  mb: 2.5,
                }}
              >
                <Box
                  sx={{
                    bgcolor: "#f5f7fb",
                    px: 2.5,
                    py: 1.5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: "1px solid #eef0f6",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      color: "#5a5f7a",
                    }}
                  >
                    Task Details <span style={{ color: "#DC2626" }}>*</span>
                  </Typography>
                  <Button
                    onClick={addTask}
                    size="small"
                    startIcon={<AddIcon sx={{ fontSize: "16px !important" }} />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      color: "#fff",
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      borderRadius: "8px",
                      px: 1.5,
                      py: 0.5,
                      boxShadow: "none",
                      "&:hover": {
                        background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)",
                        boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)",
                      },
                    }}
                  >
                    Add Task
                  </Button>
                </Box>

                <Box sx={{ p: 2.5 }}>
                  {taskDetails.map((task, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        mb: index < taskDetails.length - 1 ? 1.5 : 0,
                        flexDirection: { xs: "column", sm: "row" },
                      }}
                    >
                      {/* Task Number Badge */}
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "8px",
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          display: { xs: "none", sm: "flex" },
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          flexShrink: 0,
                        }}
                      >
                        #{index + 1}
                      </Box>
                      <TextField
                        size="small"
                        placeholder="Task name"
                        value={task.taskName}
                        onChange={(e) =>
                          handleTaskChange(index, "taskName", e.target.value)
                        }
                        fullWidth
                        required
                        variant="outlined"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            backgroundColor: "#f5f7fb",
                            fontSize: "0.875rem",
                            "& fieldset": { borderColor: "#eef0f6" },
                            "&:hover fieldset": { borderColor: "#CBD5E1" },
                            "&.Mui-focused fieldset": { borderColor: "#667eea" },
                          },
                        }}
                      />
                      <TextField
                        size="small"
                        placeholder="Hours"
                        value={task.duration}
                        onChange={(e) =>
                          handleTaskChange(index, "duration", e.target.value)
                        }
                        inputProps={{
                          pattern: "[0-9.]*",
                          inputMode: "decimal",
                        }}
                        required
                        variant="outlined"
                        sx={{
                          width: { xs: "100%", sm: 100 },
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            backgroundColor: "#f5f7fb",
                            fontSize: "0.875rem",
                            "& fieldset": { borderColor: "#eef0f6" },
                            "&:hover fieldset": { borderColor: "#CBD5E1" },
                            "&.Mui-focused fieldset": { borderColor: "#667eea" },
                          },
                        }}
                      />
                      {taskDetails.length > 1 && (
                        <IconButton
                          onClick={() => removeTask(index)}
                          size="small"
                          sx={{
                            border: "1px solid #FCA5A5",
                            borderRadius: "10px",
                            color: "#DC2626",
                            bgcolor: "#FEF2F2",
                            width: 36,
                            height: 36,
                            flexShrink: 0,
                            "&:hover": {
                              bgcolor: "#FEE2E2",
                              borderColor: "#DC2626",
                            },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                    </Box>
                  ))}

                  {/* Add Task Dashed Card */}
                  <Box
                    onClick={addTask}
                    sx={{
                      mt: 2,
                      border: "2px dashed",
                      borderImage: "linear-gradient(135deg, #667eea, #764ba2) 1",
                      borderRadius: "12px",
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        bgcolor: "#f0f0ff",
                      },
                    }}
                  >
                    <AddIcon sx={{ fontSize: 20, color: "#667eea" }} />
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#667eea" }}>
                      Add another task
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Submit Button - Full Width Gradient */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                endIcon={<ArrowForwardIcon sx={{ fontSize: "18px !important" }} />}
                sx={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  borderRadius: "12px",
                  py: 1.5,
                  boxShadow: "0 4px 16px rgba(102, 126, 234, 0.3)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)",
                    boxShadow: "0 6px 24px rgba(102, 126, 234, 0.45)",
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                Submit Timesheet
              </Button>
            </form>

            {/* Success Message */}
            {message && (
              <Box
                sx={{
                  mt: 2.5,
                  p: 2,
                  borderRadius: "12px",
                  bgcolor: "#F0FDF4",
                  border: "1px solid #BBF7D0",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#22c55e", flexShrink: 0 }} />
                <Typography
                  variant="body2"
                  sx={{ color: "#15803D", fontWeight: 500, fontSize: "0.85rem" }}
                >
                  {message}
                </Typography>
              </Box>
            )}

            {/* Error Message */}
            {error && (
              <Box
                sx={{
                  mt: 2.5,
                  p: 2,
                  borderRadius: "12px",
                  bgcolor: "#FEF2F2",
                  border: "1px solid #FECACA",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#DC2626", flexShrink: 0 }} />
                <Typography
                  variant="body2"
                  sx={{ color: "#DC2626", fontWeight: 500, fontSize: "0.85rem" }}
                >
                  {error}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </PageContainer>
  );
};

export default TimesheetForm;
